import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSiteLang as useLang } from '../siteLang';
import supabase from '../supabase';
import smiley from '../winston-smiley-white.svg';

const T = {
    fr: {
        tagline: 'Photographie et récits visuels — une plateforme pensée et construite par Longden Collective.',
        newsletter: 'Newsletter',
        newsletterDesc: 'Nouveaux albums, posts et annonces — directement par courriel.',
        placeholder: 'toi@example.com',
        consent: (link) => <>J'accepte de recevoir des courriels de winston.ie, conformément à la {link}.</>,
        privacyLink: 'politique de confidentialité',
        submitting: 'Inscription...',
        errInvalid: 'Entre un email valide.',
        errConsent: 'Le consentement est requis.',
        errDup: 'Cet email est déjà inscrit.',
        errGeneric: 'Une erreur est survenue.',
        doneMsg: 'Vérifie ta boîte mail pour confirmer.',
        explore: 'Explorer',
        announcements: 'Communiqués',
        help: 'Aide',
        privacy: 'Confidentialité',
        follow: 'Suivre',
        label: 'a Longden Collective label',
    },
    en: {
        tagline: 'Photography and visual storytelling — a platform designed and built by Longden Collective.',
        newsletter: 'Newsletter',
        newsletterDesc: 'New albums, posts and announcements — straight to your inbox.',
        placeholder: 'you@example.com',
        consent: (link) => <>I agree to receive emails from winston.ie, in accordance with the {link}.</>,
        privacyLink: 'privacy policy',
        submitting: 'Subscribing...',
        errInvalid: 'Enter a valid email.',
        errConsent: 'Consent is required.',
        errDup: 'This email is already subscribed.',
        errGeneric: 'Something went wrong.',
        doneMsg: 'Check your inbox to confirm.',
        explore: 'Explore',
        announcements: 'Announcements',
        help: 'Help',
        privacy: 'Privacy',
        follow: 'Follow',
        label: 'a Longden Collective label',
    },
    vi: {
        tagline: 'Nhiếp ảnh và kể chuyện bằng hình ảnh — nền tảng được thiết kế và xây dựng bởi Longden Collective.',
        newsletter: 'Bản tin',
        newsletterDesc: 'Album mới, bài viết và thông báo — trực tiếp vào hộp thư của bạn.',
        placeholder: 'ban@example.com',
        consent: (link) => <>Tôi đồng ý nhận email từ winston.ie, theo {link}.</>,
        privacyLink: 'chính sách bảo mật',
        submitting: 'Đang đăng ký...',
        errInvalid: 'Nhập email hợp lệ.',
        errConsent: 'Cần có sự đồng ý.',
        errDup: 'Email này đã được đăng ký.',
        errGeneric: 'Đã xảy ra lỗi.',
        doneMsg: 'Kiểm tra hộp thư để xác nhận.',
        explore: 'Khám phá',
        announcements: 'Thông báo',
        help: 'Trợ giúp',
        privacy: 'Bảo mật',
        follow: 'Theo dõi',
        label: 'a Longden Collective label',
    },
};

// Footer complet du site — toujours sur fond encre pleine (jamais
// transparent, pour ne jamais redevenir invisible sur une section claire).
// Le lockup smiley + wordmark "winston.ie" est calé à gauche, en gros
// Helvetica Neue bien défini — pas centré comme symbole isolé, mais posé
// comme une vraie signature de marque, façon fabrica/WaxPoetics.
function Footer() {
    const navigate = useNavigate();
    const { lang } = useLang();
    const t = T[lang] || T.fr;

    const [email, setEmail] = useState('');
    const [consent, setConsent] = useState(false);
    const [honeypot, setHoneypot] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (honeypot) { setDone(true); return; }
        if (!email.includes('@')) { setError(t.errInvalid); return; }
        if (!consent) { setError(t.errConsent); return; }

        setLoading(true);

        const { data: existing } = await supabase
            .from('newsletter_subscribers')
            .select('id, unsubscribed, confirmed')
            .eq('email', email)
            .maybeSingle();

        let opError;

        if (existing) {
            if (!existing.unsubscribed && existing.confirmed) {
                setLoading(false);
                setError(t.errDup);
                return;
            }
            const newToken = crypto.randomUUID();
            const { error: updateError } = await supabase
                .from('newsletter_subscribers')
                .update({
                    unsubscribed: false,
                    confirmed: false,
                    consent_given: true,
                    consent_at: new Date().toISOString(),
                    confirm_token: newToken,
                })
                .eq('id', existing.id);
            opError = updateError;
        } else {
            const { error: insertError } = await supabase
                .from('newsletter_subscribers')
                .insert({
                    email,
                    confirmed: false,
                    unsubscribed: false,
                    consent_given: true,
                    consent_at: new Date().toISOString(),
                });
            opError = insertError;
        }

        if (opError) {
            setLoading(false);
            setError(t.errGeneric);
            return;
        }

        await supabase.functions.invoke('confirm-newsletter', { body: { email } });
        setLoading(false);
        setDone(true);
    };

    return (
        <footer id="site-footer" style={{ background: '#0e1013' }}>
            <style>{`
                .footer-inner {
                    width: 100%;
                    box-sizing: border-box;
                    padding: clamp(48px, 6vw, 76px) clamp(24px, 5vw, 64px) 0;
                }
                .footer-top {
                    display: flex;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 56px;
                    padding-bottom: clamp(48px, 7vw, 88px);
                }
                .footer-heading {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 700;
                    font-size: 10.5px;
                    letter-spacing: 0.2em;
                    text-transform: uppercase;
                    color: rgba(244,239,228,0.42);
                    margin: 0 0 16px;
                }
                .footer-newsletter { flex: 1 1 380px; max-width: 460px; }
                .footer-newsletter-desc {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 500;
                    font-size: 16px;
                    line-height: 1.55;
                    color: rgba(244,239,228,0.68);
                    margin: 0 0 26px;
                    max-width: 380px;
                }
                .footer-form {
                    display: flex;
                    align-items: center;
                    border-bottom: 1.5px solid rgba(244,239,228,0.34);
                    padding-bottom: 14px;
                    max-width: 400px;
                    transition: border-color .2s ease;
                }
                .footer-form:focus-within { border-color: #f4efe4; }
                .footer-form input[type="email"] {
                    flex: 1;
                    background: none;
                    border: none;
                    outline: none;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 500;
                    font-size: 18px;
                    letter-spacing: -0.01em;
                    color: #f4efe4;
                }
                .footer-form input[type="email"]::placeholder { color: rgba(244,239,228,0.36); }
                .footer-form button {
                    background: none;
                    border: none;
                    color: #f4efe4;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 700;
                    font-size: 22px;
                    cursor: pointer;
                    padding: 0 0 0 14px;
                    white-space: nowrap;
                    transition: transform .2s ease, opacity .2s ease;
                }
                .footer-form button:hover { transform: translateX(4px); }
                .footer-form button:disabled { opacity: 0.4; cursor: default; transform: none; }
                .footer-consent {
                    display: flex;
                    align-items: flex-start;
                    gap: 9px;
                    margin: 16px 0 0;
                    max-width: 360px;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 500;
                    font-size: 11px;
                    line-height: 1.55;
                    color: rgba(244,239,228,0.42);
                    cursor: pointer;
                }
                .footer-consent input { margin-top: 2px; flex-shrink: 0; }
                .footer-consent a { color: rgba(244,239,228,0.72); text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }
                .footer-msg {
                    margin: 14px 0 0;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 500;
                    font-size: 13px;
                }

                .footer-cols {
                    display: flex;
                    gap: clamp(56px, 8vw, 128px);
                    flex: 0 0 auto;
                }
                .footer-col {
                    display: flex;
                    flex-direction: column;
                    gap: 13px;
                    min-width: 120px;
                }
                .footer-link {
                    position: relative;
                    display: inline-block;
                    width: fit-content;
                    cursor: pointer;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 500;
                    font-size: 16.5px;
                    color: rgba(244,239,228,0.78);
                    text-decoration: none;
                    transition: color .2s ease;
                }
                .footer-link::after {
                    content: '';
                    position: absolute;
                    left: 0; right: 100%; bottom: -2px;
                    height: 1px;
                    background: #f4efe4;
                    transition: right .25s ease;
                }
                .footer-link:hover { color: #f4efe4; }
                .footer-link:hover::after { right: 0; }

                .footer-brand-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 32px;
                    padding: 0 0 clamp(28px, 5vw, 40px);
                }
                .footer-wordmark {
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 700;
                    font-size: clamp(38px, 8vw, 96px);
                    letter-spacing: -0.03em;
                    color: #f4efe4;
                    line-height: 1;
                    cursor: pointer;
                    transition: opacity .2s ease;
                }
                .footer-wordmark:hover { opacity: 0.82; }
                .footer-copyright {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                    text-align: center;
                    font-family: 'Helvetica Neue', Arial, sans-serif;
                    font-weight: 700;
                    font-size: 10.5px;
                    letter-spacing: 0.16em;
                    text-transform: uppercase;
                    color: rgba(244,239,228,0.4);
                }
                .footer-copyright span:last-child { font-weight: 500; letter-spacing: 0.1em; color: rgba(244,239,228,0.3); }
                .footer-smiley {
                    height: clamp(64px, 11vw, 148px);
                    width: auto;
                    display: block;
                    cursor: pointer;
                    transition: transform .35s cubic-bezier(.2,.8,.2,1);
                }
                .footer-smiley:hover { transform: scale(1.06) rotate(-4deg); }

                @media (max-width: 860px) {
                    .footer-top { gap: 44px; }
                }
                @media (max-width: 720px) {
                    .footer-inner { padding: clamp(56px, 14vw, 72px) 28px 0; }

                    .footer-top { flex-direction: column; gap: 56px; padding-bottom: 56px; }
                    .footer-heading { font-size: 11px; margin-bottom: 18px; }

                    .footer-newsletter { flex: 0 0 auto; max-width: 100%; }
                    .footer-newsletter-desc { font-size: 15.5px; max-width: 100%; margin-bottom: 28px; }

                    .footer-form { max-width: 100%; padding-bottom: 16px; }
                    .footer-form input[type="email"] { font-size: 17px; }
                    .footer-form button { font-size: 24px; }

                    .footer-consent { max-width: 100%; margin-top: 20px; font-size: 12px; }

                    .footer-cols { flex: 0 0 auto; width: 100%; gap: 28px; }
                    .footer-col { gap: 17px; min-width: 0; flex: 1 1 0; }
                    .footer-link { font-size: 15.5px; }

                    .footer-brand-row {
                        flex-wrap: wrap;
                        align-items: flex-start;
                        row-gap: 18px;
                        padding-bottom: 44px;
                    }
                    .footer-wordmark { flex-basis: 100%; font-size: clamp(46px, 15vw, 60px); }
                    .footer-copyright { align-items: flex-start; text-align: left; }
                    /* Une seule marque à la fois sur mobile : le wordmark suffit,
                       le smiley (déjà répété dans la navbar) est superflu ici. */
                    .footer-smiley { display: none; }
                }
            `}</style>

            <div className="footer-inner">
                <div className="footer-top">
                    <div className="footer-newsletter">
                        <p className="footer-heading">{t.newsletter}</p>
                        <p className="footer-newsletter-desc">{t.newsletterDesc}</p>

                        {done ? (
                            <p className="footer-msg" style={{ color: 'rgba(244,239,228,0.75)' }}>{t.doneMsg}</p>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <input
                                    type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
                                    tabIndex="-1" autoComplete="off"
                                    style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                                />
                                <div className="footer-form">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        placeholder={t.placeholder}
                                        required
                                    />
                                    <button type="submit" disabled={loading}>
                                        {loading ? t.submitting : '→'}
                                    </button>
                                </div>
                                <label className="footer-consent">
                                    <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
                                    <span>
                                        {t.consent(
                                            <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>{t.privacyLink}</a>
                                        )}
                                    </span>
                                </label>
                                {error && <p className="footer-msg" style={{ color: '#c96a5e' }}>{error}</p>}
                            </form>
                        )}
                    </div>

                    <div className="footer-cols">
                        <div className="footer-col">
                            <p className="footer-heading">{t.explore}</p>
                            <span className="footer-link" onClick={() => navigate('/communiques')}>{t.announcements}</span>
                            <span className="footer-link" onClick={() => navigate('/help')}>{t.help}</span>
                            <span className="footer-link" onClick={() => navigate('/privacy')}>{t.privacy}</span>
                        </div>
                        <div className="footer-col">
                            <p className="footer-heading">{t.follow}</p>
                            <a className="footer-link" href="https://www.instagram.com/winston.ie/" target="_blank" rel="noreferrer">Instagram</a>
                            <a className="footer-link" href="https://youtube.com/@winston-ie" target="_blank" rel="noreferrer">YouTube</a>
                        </div>
                    </div>
                </div>

                <div className="footer-brand-row">
                    <span className="footer-wordmark" onClick={() => navigate('/')}>winston.ie</span>

                    <div className="footer-copyright">
                        <span>© winston.ie 2026</span>
                        <span>{t.label}</span>
                    </div>

                    <img src={smiley} alt="winston.ie" className="footer-smiley" onClick={() => navigate('/')} />
                </div>
            </div>
        </footer>
    );
}

export default Footer;